import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  FormControl,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { getAllPurchaseOrders } from './functions/get_all_purchase_orders.function';
import { deletePurchaseOrder } from './functions/delete_purchase_order.function';
import { notify } from '../../common/functions/react-toastify.function';
import { getPoRingsDetail } from '../VendorInvoiceManagement/functions/get_po_rings_detail.function';

export const PurchaseOrderTable = forwardRef(
  ({ onEdit, onDeleteSuccess, onUploadRingsDetail }, ref) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewDialog, setViewDialog] = useState({
      open: false,
      po: null,
      rings: [],
      loading: false,
    });

    // Get unique values for filters from purchase orders data
    const vendors = useMemo(() => {
      const uniqueVendors = [
        ...new Set(purchaseOrders.map((po) => po.finalizedVender).filter(Boolean)),
      ];
      return uniqueVendors.sort();
    }, [purchaseOrders]);

    const cities = useMemo(() => {
      const uniqueCities = [...new Set(purchaseOrders.map((po) => po.city).filter(Boolean))];
      return uniqueCities.sort();
    }, [purchaseOrders]);

    const types = useMemo(() => {
      const uniqueTypes = [...new Set(purchaseOrders.map((po) => po.type).filter(Boolean))];
      return uniqueTypes.sort();
    }, [purchaseOrders]);

    // Function to map API response to table format
    const mapApiDataToTableFormat = (apiData) => {
      if (!Array.isArray(apiData)) {
        return [];
      }

      return apiData.map((item) => {
        // Parse unit_rate_meter (can be string "500" or "500 PKR")
        let unitRateMeter = null;
        if (item.unit_rate_meter) {
          const unitRateMatch = String(item.unit_rate_meter).match(/(\d+)/);
          unitRateMeter = unitRateMatch
            ? parseFloat(unitRateMatch[1])
            : parseFloat(item.unit_rate_meter);
        }

        // Format dates from ISO string to YYYY-MM-DD
        const formatDate = (dateString) => {
          if (!dateString) {
            return null;
          }
          try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          } catch {
            return dateString;
          }
        };

        return {
          id: item.id,
          account: item.account || null,
          item: item.item || null,
          type: item.type || null,
          city: item.city || null,
          finalLengthKm: item.final_length_km ? parseFloat(item.final_length_km) : null,
          finalLengthMeters: item.final_length_meters ? parseFloat(item.final_length_meters) : null,
          unitRateMeter: unitRateMeter,
          totalCostWithoutPST: item.total_cost_without_pst
            ? parseFloat(item.total_cost_without_pst)
            : null,
          finalizedVender: item.finalized_vendor || item.finalizedVender || null,
          pr: item.pr || null,
          po: item.po || null,
          poDate: formatDate(item.po_date),
          requestDate: formatDate(item.request_date),
          uploadedBy: item.uploaded_by || null,
          uploadedAt: item.uploaded_at || null,
        };
      });
    };

    // Function to fetch purchase orders data
    const fetchPurchaseOrders = async () => {
      setLoading(true);
      try {
        const apiData = await getAllPurchaseOrders();
        const mappedData = mapApiDataToTableFormat(apiData);
        setPurchaseOrders(mappedData);
      } catch (err) {
        console.error('Error fetching purchase orders:', err);
        setPurchaseOrders([]);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch on component mount
    useEffect(() => {
      fetchPurchaseOrders();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Function to remove purchase order from the table
    const removePurchaseOrder = (id) => {
      setPurchaseOrders((prev) => {
        // Convert both IDs to numbers for comparison to handle type mismatches
        const idToRemove = Number(id);
        return prev.filter((item) => Number(item.id) !== idToRemove);
      });
    };

    // Function to add or update purchase orders from upload response
    const addOrUpdatePurchaseOrders = (uploadResponseData) => {
      if (!Array.isArray(uploadResponseData) || uploadResponseData.length === 0) {
        return;
      }

      // Map the upload response data to table format
      const mappedUploadData = mapApiDataToTableFormat(uploadResponseData);

      // Merge with existing records: replace if ID exists, add if new
      setPurchaseOrders((prev) => {
        const updated = [...prev];

        mappedUploadData.forEach((newRecord) => {
          const existingIndex = updated.findIndex((item) => item.id === newRecord.id);
          if (existingIndex >= 0) {
            // Replace existing record
            updated[existingIndex] = newRecord;
          } else {
            // Add new record
            updated.push(newRecord);
          }
        });

        return updated;
      });
    };

    // Function to update a single purchase order from update API response
    const updatePurchaseOrder = (updateResponseData) => {
      if (!updateResponseData || !updateResponseData.id) {
        return;
      }

      // Map the update response data to table format
      const mappedUpdateData = mapApiDataToTableFormat([updateResponseData]);

      if (mappedUpdateData.length === 0) {
        return;
      }

      const updatedRecord = mappedUpdateData[0];

      // Update the record in the table
      setPurchaseOrders((prev) => {
        const updated = [...prev];
        const existingIndex = updated.findIndex(
          (item) => Number(item.id) === Number(updatedRecord.id)
        );

        if (existingIndex >= 0) {
          // Replace existing record
          updated[existingIndex] = updatedRecord;
        }

        return updated;
      });
    };

    // Function to download purchase orders as Excel file
    const handleDownloadExcel = (tableInstance) => {
      // Get filtered rows from the table instance (includes all filters: custom + MUI column filters)
      // If table instance is provided, use filtered rows; otherwise fallback to filteredPurchaseOrders
      let dataToDownload = filteredPurchaseOrders;

      if (tableInstance) {
        const filteredRows = tableInstance.getFilteredRowModel().rows;
        dataToDownload = filteredRows.map((row) => row.original);
      }

      if (dataToDownload.length === 0) {
        return;
      }

      try {
        // Map table data to Excel format matching upload template
        const excelData = dataToDownload.map((po) => {
          // Format unit rate - ensure it has PKR suffix
          const unitRate = po.unitRateMeter
            ? typeof po.unitRateMeter === 'number'
              ? `${po.unitRateMeter} PKR`
              : po.unitRateMeter.toString().includes('PKR')
                ? po.unitRateMeter.toString()
                : `${po.unitRateMeter} PKR`
            : '';

          // Format dates to YYYY-MM-DD
          const formatDate = (dateString) => {
            if (!dateString) {
              return '';
            }
            try {
              if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return dateString;
              }
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            } catch {
              return dateString;
            }
          };

          return {
            Account: po.account || '',
            Item: po.item || '',
            Type: po.type || '',
            City: po.city || '',
            'Final length (km)': po.finalLengthKm?.toString() || '',
            'Final length (meters)': po.finalLengthMeters?.toString() || '',
            'Unit Rate (meter)': unitRate,
            'Total Cost without PST PKR': po.totalCostWithoutPST?.toString() || '',
            'Finalized Vendor': po.finalizedVender || '',
            PR: po.pr || '',
            PO: po.po || '',
            'PO Date': formatDate(po.poDate),
            'Request Date': formatDate(po.requestDate),
          };
        });

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Orders');

        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `Purchase_Orders_${currentDate}.xlsx`;

        // Download file
        XLSX.writeFile(workbook, filename);
      } catch (error) {
        console.error('Error downloading Excel file:', error);
      }
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      refetchPurchaseOrders: fetchPurchaseOrders,
      removePurchaseOrder,
      addOrUpdatePurchaseOrders,
      updatePurchaseOrder,
    }));

    const filteredPurchaseOrders = useMemo(() => {
      let filtered = purchaseOrders;

      // Filter by vendor
      if (selectedVendor) {
        filtered = filtered.filter((po) => po.finalizedVender === selectedVendor);
      }

      // Filter by city
      if (selectedCity) {
        filtered = filtered.filter((po) => po.city === selectedCity);
      }

      // Filter by type
      if (selectedType) {
        filtered = filtered.filter((po) => po.type === selectedType);
      }

      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter((po) =>
          Object.values(po).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      return filtered;
    }, [selectedVendor, selectedCity, selectedType, searchTerm, purchaseOrders]);

    // eslint-disable-next-line react/prop-types
    const ActionsMenu = ({ row }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const [deleting, setDeleting] = useState(false);
      const open = Boolean(anchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleDelete = async () => {
        try {
          setDeleting(true);
          const result = await deletePurchaseOrder(row.original.id, row.original.po);

          if (result.success && result.data) {
            // Remove the record from table using the ID from the delete response
            const deletedId = result.data.id;

            // Ensure we remove the record - use the ID from response or fallback to row ID
            const idToRemove = deletedId !== undefined ? deletedId : row.original.id;
            removePurchaseOrder(idToRemove);

            notify('success', result.message || 'PO record deleted successfully');

            if (onDeleteSuccess) {
              onDeleteSuccess();
            }
          }
        } catch (err) {
          console.error('Delete failed:', err);
          notify('error', 'Failed to delete purchase order. Please try again.');
        } finally {
          setDeleting(false);
          handleClose();
        }
      };

      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={async () => {
                const poRow = row.original;
                setViewDialog({ open: true, po: poRow, rings: [], loading: true });
                try {
                  const res = await getPoRingsDetail(poRow?.id);
                  if (!res.success) {
                    notify('error', res.error || 'Failed to fetch rings detail');
                    setViewDialog({ open: true, po: poRow, rings: [], loading: false });
                    return;
                  }
                  setViewDialog({
                    open: true,
                    po: poRow,
                    rings: Array.isArray(res.data) ? res.data : [],
                    loading: false,
                  });
                } catch (e) {
                  console.error('Failed to fetch rings detail:', e);
                  notify('error', 'Failed to fetch rings detail');
                  setViewDialog({ open: true, po: poRow, rings: [], loading: false });
                }
              }}
              disabled={deleting}
              size="small"
              title="View PO & Ring details"
            >
              <VisibilityOutlinedIcon sx={{ fontSize: '18px', color: '#667085' }} />
            </IconButton>
            <IconButton onClick={handleClick} disabled={deleting} size="small">
              <MoreVertIcon sx={{ fontWeight: 300, fontSize: '18px', color: '#667085' }} />
            </IconButton>
          </Box>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                // eslint-disable-next-line react/prop-types
                onEdit(row.original);
                handleClose();
              }}
              disabled={deleting}
            >
              Edit
            </MenuItem>
            {onUploadRingsDetail && (
              <MenuItem
                onClick={() => {
                  // eslint-disable-next-line react/prop-types
                  onUploadRingsDetail(row.original);
                  handleClose();
                }}
                disabled={deleting}
              >
                Upload rings/section detail
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </MenuItem>
          </Menu>
        </>
      );
    };

    const columns = useMemo(
      () => [
        {
          accessorKey: 'account',
          header: 'Account',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const account = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{account || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'item',
          header: 'Item',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const item = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{item || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'type',
          header: 'Type',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const type = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{type || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'city',
          header: 'City',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const city = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{city || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'finalLengthKm',
          header: 'Final length (km)',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const length = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{length || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'finalLengthMeters',
          header: 'Final length (meters)',
          size: 200,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const length = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{length || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'unitRateMeter',
          header: 'Unit Rate (meter)',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const rate = cell.getValue();
            return (
              <span style={{ fontSize: '14px', color: '#667085' }}>
                {rate ? `PKR ${rate?.toLocaleString()}` : 'N/A'}
              </span>
            );
          },
        },
        {
          accessorKey: 'totalCostWithoutPST',
          header: 'Total Cost without PST PKR',
          size: 220,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const cost = cell.getValue();
            return (
              <span style={{ fontSize: '14px', color: '#667085' }}>
                {cost ? `PKR ${cost?.toLocaleString()}` : 'N/A'}
              </span>
            );
          },
        },
        {
          accessorKey: 'finalizedVender',
          header: 'Finalized Vendor',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const vendor = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{vendor || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'pr',
          header: 'PR',
          size: 120,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const pr = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{pr || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'po',
          header: 'PO',
          size: 120,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const po = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{po || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'poDate',
          header: 'PO Date',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const date = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'requestDate',
          header: 'Request Date',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const date = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
          },
        },
        {
          header: 'Actions',
          enableColumnActions: false,
          size: 50,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => <ActionsMenu row={row} />,
        },
      ],
      []
    );

    const table = useMaterialReactTable({
      columns,
      data: filteredPurchaseOrders,
      state: { isLoading: loading },
      muiCircularProgressProps: {
        color: 'inherit',
        size: 40,
        sx: { color: '#999999' },
      },
      enableGlobalFilter: false,
      enableGlobalFilterModes: false,
      positionGlobalFilter: 'none',
      enableColumnFilterModes: false,
      enableColumnOrdering: false,
      enableGrouping: false,
      enableRowSelection: false,
      enableSelectAll: false,
      enableColumnPinning: false,
      enableFacetedValues: false,
      enableSorting: false,
      enableDensityToggle: false,
      paginationDisplayMode: 'pages',
      positionToolbarAlertBanner: 'bottom',
      initialState: {
        pagination: {
          pageSize: 10,
        },
        columnOrder: [
          'account',
          'item',
          'type',
          'city',
          'finalLengthKm',
          'finalLengthMeters',
          'unitRateMeter',
          'totalCostWithoutPST',
          'finalizedVender',
          'pr',
          'po',
          'poDate',
          'requestDate',
          'actions',
        ],
      },
      muiSearchTextFieldProps: {
        size: 'small',
        variant: 'outlined',
      },
      muiPaginationProps: {
        color: 'secondary',
        rowsPerPageOptions: [10, 20, 30, 40],
        shape: 'rounded',
        variant: 'outlined',
        sx: {
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: '#BD1D23',
            color: 'white',
          },
        },
      },
      enableBottomToolbar: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      enablePagination: true,
      muiTableBodyRowProps: {
        sx: {
          height: 52,
          minHeight: 52,
          maxHeight: 52,
        },
      },
      muiTableBodyCellProps: {
        sx: {
          color: '#667085',
          backgroundColor: '#fff',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
          padding: '0 12px',
          '&:first-of-type': {
            paddingLeft: '12px !important',
          },
        },
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#344054',
          backgroundColor: '#E8E9E9',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '125%',
          height: '56px',
          padding: '0 12px',
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: '18px !important',
          },
        },
      },
      muiTablePaperProps: {
        sx: {
          boxShadow: 'none',
        },
      },
      muiTableHeadRowProps: {
        sx: {
          height: 56,
          '& .MuiTableCell-root': {
            padding: '6px 12px',
            verticalAlign: 'middle',
          },
        },
      },
      renderTopToolbarCustomActions: ({ table }) => (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {/* Left side - Search field and Download button */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              sx={{
                backgroundColor: '#fff',
                width: '300px',
                '& .MuiInputBase-input': {
                  color: 'gray',
                  '::placeholder': {
                    color: '#b3b9c4',
                    opacity: 1,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  border: '1px solid #ccc',
                  borderRadius: '10px',
                  paddingRight: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Download Button - Right next to search field */}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadExcel(table)}
              disabled={filteredPurchaseOrders.length === 0}
              sx={{
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#333',
                borderRadius: '10px',
                textTransform: 'none',
                padding: '8px 16px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid #ccc',
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  border: '1px solid #ccc',
                  color: '#999',
                  opacity: 0.6,
                },
              }}
            >
              Download
            </Button>
          </Box>

          {/* Right side - Filter dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Vendor Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  color: '#757575',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '&:hover': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  VENDOR
                </MenuItem>
                {vendors.map((vendor, index) => (
                  <MenuItem
                    key={vendor}
                    value={vendor}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {vendor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* City Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  color: '#757575',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '&:hover': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  CITY
                </MenuItem>
                {cities.map((city, index) => (
                  <MenuItem
                    key={city}
                    value={city}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== cities.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Type Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  color: '#757575',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '&:hover': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  TYPE
                </MenuItem>
                {types.map((type, index) => (
                  <MenuItem
                    key={type}
                    value={type}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== types.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      ),
    });

    return (
      <div className="purchase-order-table-main-container">
        <Dialog
          open={viewDialog.open}
          onClose={() => setViewDialog({ open: false, po: null, rings: [], loading: false })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              PO Details & Ring Details — {viewDialog.po?.po || '—'}
            </Typography>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            {(() => {
              const poDetails = viewDialog.po || {};

              const formatPoLength = () => {
                const km = poDetails.finalLengthKm;
                const m = poDetails.finalLengthMeters;
                if (km != null && !Number.isNaN(Number(km))) {
                  return `${Number(km).toLocaleString()} km`;
                }
                if (m != null && !Number.isNaN(Number(m))) {
                  return `${Number(m).toLocaleString()} m`;
                }
                return 'N/A';
              };

              const toNum = (v) => {
                const n = Number(String(v ?? '').replace(/,/g, ''));
                return Number.isFinite(n) ? n : 0;
              };

              const ringTotals = (viewDialog.rings || []).reduce(
                (acc, ring) => {
                  acc.total += toNum(ring.total_length_meters ?? ring.totalLengthMeters);
                  acc.onNet += toNum(ring.on_net_meters ?? ring.onNetMeters);
                  acc.offNet += toNum(ring.off_net_meters ?? ring.offNetMeters);
                  return acc;
                },
                { total: 0, onNet: 0, offNet: 0 }
              );

              return (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    PO Information
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 1.5,
                      mb: 3,
                      p: 1.5,
                      bgcolor: '#fafafa',
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        PO Number
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.po ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        PO Date
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.poDate ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Vendor
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.finalizedVender ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        City
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.city ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Item
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.item ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <br />
                      <Typography variant="body2">{poDetails.type ?? 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        PO Length
                      </Typography>
                      <br />
                      <Typography variant="body2">{formatPoLength()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Unit Rate (per m)
                      </Typography>
                      <br />
                      <Typography variant="body2">
                        {poDetails.unitRateMeter != null
                          ? `PKR ${Number(poDetails.unitRateMeter).toLocaleString()}`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Cost
                      </Typography>
                      <br />
                      <Typography variant="body2">
                        {poDetails.totalCostWithoutPST != null
                          ? `PKR ${Number(poDetails.totalCostWithoutPST).toLocaleString()}`
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Ring Details
                  </Typography>

                  {viewDialog.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : viewDialog.rings.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No ring details uploaded for this PO.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Ring Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Total Length (m)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              On Net (m)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Off Net (m)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewDialog.rings.map((ring, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{ring.ring_unique_name ?? '—'}</TableCell>
                              <TableCell align="right">{ring.total_length_meters ?? '—'}</TableCell>
                              <TableCell align="right">{ring.on_net_meters ?? '—'}</TableCell>
                              <TableCell align="right">{ring.off_net_meters ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              Totals
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {ringTotals.total.toLocaleString()} m
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {ringTotals.onNet.toLocaleString()} m
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {ringTotals.offNet.toLocaleString()} m
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setViewDialog({ open: false, po: null, rings: [], loading: false })}
              variant="outlined"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <div className="purchase-order-table-container">
          <div className="purchase-order-table-wrapper">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
    );
  }
);

PurchaseOrderTable.displayName = 'PurchaseOrderTable';

PurchaseOrderTable.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDeleteSuccess: PropTypes.func,
  onUploadRingsDetail: PropTypes.func,
};
